import { type CSSObject } from '@emotion/react';
import { useTheme } from '@doxyme/ui/theme';
import { textareaClasses } from '@doxyme/ui/components/foundations';

export const useTextboxStyles = ({
  hasError,
  textareaHeight,
}: {
  hasError: boolean;
  disabled?: boolean;
  textareaHeight?: string | number;
}): {
  wrapper: CSSObject;
  textarea: CSSObject;
  staticContent: CSSObject;
} => {
  const theme = useTheme();
  return {
    wrapper: {
      // Override textarea styles
      [`div.${textareaClasses.wrapper}`]: {
        boxShadow: 'none',
        borderRadius: 0,
        [`&:hover, &.${textareaClasses.focused}`]: {
          borderColor: 'transparent',
          boxShadow: 'none',
          outline: 'none',
        },
      },

      // Re-apply to wrapper
      position: 'relative',
      overflow: 'hidden',
      borderRadius: theme.shape.radius8,
      outline: '0px solid transparent',
      border: `1px solid transparent`,
      boxShadow: `${theme.typography.inputShadow}, 0 0 0 1px ${
        hasError ? theme.input.border.error : theme.input.border.default
      }`,

      '&:hover': {
        borderColor: 'transparent',
        boxShadow: `${theme.typography.inputShadow}, 0 0 0 ${
          hasError ? 2 : 1
        }px ${
          hasError ? theme.input.border.errorActive : theme.input.border.active
        }`,
        outlineWidth: hasError ? 1 : 0,
        outlineColor: theme.input.border.errorActive,
        '&:not:has(:disabled))~svg': {
          color: hasError ? theme.icon.secondary : theme.icon.brandlight,
        },
      },

      [`&:has(.${textareaClasses.disabled})`]: {
        borderColor: 'transparent',
        backgroundColor: theme.input.background.disabled,
        boxShadow: `${theme.typography.inputShadow}, 0 0 0 1px ${theme.input.border.default}`,
      },

      [`&:has(.${textareaClasses.focused}:not(.${textareaClasses.disabled}))`]:
        {
          borderColor: 'transparent',
          boxShadow: `${theme.typography.inputShadow}, 0 0 0 2px ${
            hasError
              ? theme.input.border.errorActive
              : theme.input.border.active
          }`,
          outlineWidth: theme.spacing(1),
          outlineColor: hasError
            ? theme.input.border.errorFocused
            : theme.input.border.focused,
          '&~svg': {
            color: hasError ? theme.icon.secondary : theme.icon.brand,
          },
        },
    },
    textarea: {
      minHeight: textareaHeight,
    },
    staticContent: {
      lineHeight: '20px',
      padding: theme.spacing(3),
      background: theme.input.background.disabled,
      borderTop: `1px solid ${theme.border.primary}`,
      color: theme.text.generic.tertiary,
      a: {
        color: theme.text.generic.tertiary,
      },
      whiteSpace: 'pre-wrap',
    },
  };
};
